import {
  HubDto,
  KnownDeliveryDtoDeliveryType,
  KnownHubDetailsDtoType,
  KnownProjectModuleSettingsDtoStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { HubDetails } from '../../models/hub-details.model';
import { DeliveryContainerHoverCard } from './delivery-container-hover-card.component';
import { KioskTileContent } from '../../../../ui/components/pure/virtual-hub/kiosk-tile-content/kiosk-tile-content.component';
import { DeliveryContainerTileContent } from '../../../../ui/components/pure/virtual-hub/delivery-container-tile-content/delivery-container-tile-content.component';
import { useHistory } from 'react-router-dom';
import { Paths, createPath } from '../../../../app/router/app.router';
import { buildSettingsDeepLink } from '../../../settings/deep-link/settings-deep-link';
import { SettingsTreeDataRequestType } from '../../../settings/reducer/settings-tree-reducer.types';
import type { useLockersList } from '../hooks/use-lockers-list.hook';
import { HubDetailsActions, makeDefineHubDetailsActions } from '../actions/hub-details.actions';
import { HubDetailsActionsObserver } from './hub-details-actions-observer.component';
import { useCallback, useMemo, useState } from 'react';
import { useHasDoubleSidedTower } from '../hooks/use-has-double-sided-tower.hook';
import { Space } from '../../../../ui/components/pure/icons/Space.icon';
import { LockerDetailsActions, makeDefineLockerDetailsActions } from '../../details/actions/locker-details.actions';
import { useQueriesGetProjectModules } from '../../../project-modules/queries';
import {
  mapProjectModuleToExternalDeliveryType,
  mapProjectModuleToInternalDeliveryType
} from '../../../project-modules/mappings';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import {
  HubCardWithTowers,
  HubCardWithTowersModeType,
  TransactionTypeHoverConfigMode
} from '../../../../ui/components/pure/virtual-hub/hub-card-with-towers/hub-card-with-towers.component';
import { ScreenOrientation } from '../../../../ui/components/pure/virtual-hub/kiosk-tile-content/kiosk-tile-content.component';
import {
  mapTowerDetailsToRenderData,
  mapSectionDetailsToRenderData
} from '../../../../ui/components/pure/virtual-hub/tower-renderer/tower-render-data.hub-details.mapper';
import { LockerRenderData } from '../../../../ui/components/pure/virtual-hub/tower-renderer/tower-renderer.types';
import { DeliveryContainer } from '../../models/delivery-container.model';
import { LockerDetails } from '../../models/locker-details.model';

type HubDetailsProps = {
  projectID: string;
  hub: HubDto;
  hubDetails: HubDetails;
  sideSelectOptions: string[];
  selectedSide: { hubID: string; selectedSide: string | undefined; isInverted: boolean } | undefined;
  onSelectSide(label: string): void;
  deliveryContainerActions: ReturnType<typeof useLockersList>['actions'];
  setDeliveryContainerAction: (action: LockerDetailsActions | null, payload?: any) => void;
};

export function HubDetailsCard({
  projectID,
  hub,
  hubDetails,
  deliveryContainerActions,
  setDeliveryContainerAction,
  onSelectSide,
  selectedSide,
  sideSelectOptions
}: HubDetailsProps) {
  const projectModules = useQueriesGetProjectModules(hub.acceptsDeliveriesForProjectIDs);

  const towerModelNames = useMemo(() => (hubDetails.towers ?? []).map((t) => t.modelName), [hubDetails.towers]);
  const { hasDoubleSided: isDoubleSided, towerModels } = useHasDoubleSidedTower(towerModelNames);

  const transactionTypes = useMemo(() => {
    const projectModuleNames = [
      ...new Set(
        projectModules
          .flatMap((query) => (query.data ? query.data : []))
          .filter((m) => m.data.status === KnownProjectModuleSettingsDtoStatus.Active)
          .map((m) => m.schema.name)
          .sort((a, b) => a.localeCompare(b))
      )
    ];
    const types = projectModuleNames
      .flatMap((name) => [mapProjectModuleToExternalDeliveryType(name), mapProjectModuleToInternalDeliveryType(name)])
      .filter((t): t is KnownDeliveryDtoDeliveryType => t !== null);
    return types;
  }, [projectModules]);

  const transactionTypeProps = useMemo(() => {
    if (transactionTypes.length === 0) {
      return undefined;
    }
    return transactionTypes.map((t) => ({
      value: t,
      icon: deliveryTypeToIconMap[t]
    }));
  }, [transactionTypes]);

  const history = useHistory();
  const [currentHubAction, setCurrentHubAction] = useState<HubDetailsActions | null>(null);
  const setHubAction = useCallback(
    (action: HubDetailsActions | null) => {
      // "Edit settings" navigates to the managing project's settings page (which may differ from the
      // current one) and deep-links to this hub's node, rather than opening a modal like other actions.
      if (action === HubDetailsActions.EDIT_SETTINGS) {
        history.push(
          buildSettingsDeepLink(hub.managedByProjectID, {
            entityId: hub.id,
            scope: hub.managedByProjectID,
            nodeType: SettingsTreeDataRequestType.ProjectChildren,
            label: hub.name
          })
        );
        return;
      }
      setCurrentHubAction(action);
    },
    [history, hub.managedByProjectID, hub.id, hub.name]
  );
  const hubActions = { currentAction: currentHubAction, setAction: setHubAction };
  const hubActionItems = makeDefineHubDetailsActions(hub, projectID)(setHubAction);

  const kioskScreenOrientation = useMemo<ScreenOrientation>(() => {
    if (hubDetails.type === KnownHubDetailsDtoType.LockersHubExternal) {
      return ScreenOrientation.Landscape;
    }
    return ScreenOrientation.Portrait;
  }, [hubDetails.type]);

  // Build lookup map from locker/space ID to the original model for tooltips and custom content
  const deliveryContainerMap = useMemo(() => {
    const map = new Map<string, DeliveryContainer>();
    hubDetails.towers?.forEach((tower) => tower.lockers.forEach((locker) => map.set(locker.id, locker)));
    hubDetails.sections?.forEach((section) => section.spaces.forEach((space) => map.set(space.id, space)));
    return map;
  }, [hubDetails.towers, hubDetails.sections]);

  // Map sections to TowerRenderData
  const sectionRenderData = useMemo(() => {
    if (!hubDetails.sections) return [];
    return hubDetails.sections.map((section) => mapSectionDetailsToRenderData(section));
  }, [hubDetails.sections]);

  // Map towers to TowerRenderData — inversion and blank single-sided handled by HubCardWithTowers
  const towerRenderData = useMemo(() => {
    if (!hubDetails.towers) return [];
    return hubDetails.towers.map((tower) => {
      const data = mapTowerDetailsToRenderData(tower, hubDetails.type);
      const model = towerModels.find((m) => m.name === tower.modelName);
      data.isDoubleSided = model?.isDoubleSided ?? false;
      return data;
    });
  }, [hubDetails.towers, towerModels, hubDetails.type]);

  const acceptsTransactionType = useCallback(
    (lockerId: string, type: string) => {
      const container = deliveryContainerMap.get(lockerId);
      return !!container?.acceptsDeliveryTransactionType(type as KnownDeliveryDtoDeliveryType);
    },
    [deliveryContainerMap]
  );

  const allModules = useMemo(() => [...sectionRenderData, ...towerRenderData], [sectionRenderData, towerRenderData]);

  const renderTooltipContent = useCallback(
    (lockerId: string) => {
      const container = deliveryContainerMap.get(lockerId);
      if (!container || container.isKiosk) return undefined;
      const actions = makeDefineLockerDetailsActions(
        projectID,
        lockerId,
        hubDetails,
        hub,
        true,
        false
      )(setDeliveryContainerAction);
      return <DeliveryContainerHoverCard deliveryContainer={container} actions={actions} />;
    },
    [projectID, hubDetails, hub, setDeliveryContainerAction, deliveryContainerMap]
  );

  const renderLockerContent = useCallback(
    (locker: LockerRenderData) => {
      const container = deliveryContainerMap.get(locker.id);
      if (!container) return undefined;
      if (container.isKiosk) {
        return (
          <KioskTileContent
            screenOnly={locker.sizeName === 'space'}
            hubType={hubDetails.type}
            kioskScreenOrientation={kioskScreenOrientation}
          />
        );
      }
      if (locker.sizeName === 'space') {
        return (
          <DeliveryContainerTileContent
            deliveryContainerName={locker.name}
            reservedForRecipients={container.reservedForRecipients}
            Icon={Space}
          />
        );
      }
      const lockerModel = container as LockerDetails;
      return (
        <DeliveryContainerTileContent
          deliveryContainerName={locker.name}
          reservedForRecipients={container.reservedForRecipients}
          Icon={locker.sizeIcon}
          minimiseReserveeChip={lockerModel.sizeName === 'XS'}
          reserveeChipVerticalPosition={['XS', 'S'].includes(lockerModel.sizeName) ? 'center' : 'bottom'}
        />
      );
    },
    [deliveryContainerMap, hubDetails.type, kioskScreenOrientation]
  );

  const makeLinkUrl = useCallback(
    (lockerId: string) => {
      return createPath(Paths.container, { projectID, deliveryContainerID: lockerId });
    },
    [projectID]
  );

  return (
    <>
      <HubCardWithTowers
        key={hubDetails.id}
        headerProps={{
          hubName: hubDetails.name,
          hubStatus: hubDetails.status,
          kioskLastConfigUpdatedTime: hubDetails.kiosk?.lastConfigUpdatedTime,
          statusIcon: hubDetails.statusIcon,
          statusColor: hubDetails.statusColor,
          deliveryContainerCount: hubDetails.deliveryContainersCount,
          deliveryContainerType: hubDetails.deliveryContainerType,
          deliveryContainerCountsBySize: hubDetails.deliveryContainerCountsSortedBySize,
          actions: hubActionItems
        }}
        towers={allModules}
        maxModuleHeight={hubDetails.maxModuleHeight}
        isDoubleSided={isDoubleSided}
        onSelectSide={onSelectSide}
        selectedSide={selectedSide?.selectedSide}
        sideSelectOptions={sideSelectOptions}
        mode={{
          type: HubCardWithTowersModeType.Interactive,
          hideTooltip: deliveryContainerActions.currentAction !== null,
          renderTooltipContent,
          renderLockerContent,
          makeLinkUrl,
          makeLinkRequiresPermissions: (locker) => locker.isKiosk
        }}
        transactionTypes={transactionTypeProps}
        transactionTypeHover={{ mode: TransactionTypeHoverConfigMode.Outline, accepts: acceptsTransactionType }}
        kioskScreenOrientation={kioskScreenOrientation}
      />
      <HubDetailsActionsObserver hub={hub} actions={hubActions} projectID={projectID} />
    </>
  );
}
